import React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ui";
import Seo from "@/components/Seo";
import { ImmersiveShell } from "@/components/immersive/ImmersiveShell";
import { Reveal } from "@/components/immersive/Reveal";
import { CtaCluster, CtaLink, PromoBanner } from "@/components/immersive/MarketingBits";
import { SITE_META, SERVICES, MATCH_STEPS, WHY, FAQS } from "@/lib/content";

const HERO_PARAS = [
  "HomecareMatches makes finding the right home care easy. Tell us your care needs, preferences, schedule, and location — just once.",
  "We reach out to our network of partner home-care agencies to help find an available caregiver who may be a good match. No calling agency after agency and repeating your story each time.",
  "Not sure what type of care you need? Book an appointment with a representative for guidance, and get started with promo code LINKED for up to 8 hours free.",
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "Organization", "name": "HomecareMatches", "url": "https://homecarematches.com/", "logo": "https://homecarematches.com/homecarematches-logo.png", "description": SITE_META.description },
    { "@type": "WebSite", "name": "HomecareMatches", "url": "https://homecarematches.com/" },
    { "@type": "FAQPage", "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
  ],
};

export default function Landing() {
  return (
    <ImmersiveShell>
      <Seo title="" path="/" description={SITE_META.description} ogDescription={SITE_META.ogDescription} twitterDescription={SITE_META.twitterDescription} keywords={SITE_META.keywords} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(JSON_LD) }} />

      <section className="imm-hero imm-wrap" data-testid="landing-screen">
        <Reveal className="imm-eyebrow" delay={100}>Home care services · Senior care matching</Reveal>
        <Reveal as="h1" delay={220}>Home Care Services<span className="soft"> Made Easy</span></Reveal>
        {HERO_PARAS.map((p, i) => (
          <Reveal key={i} as="p" className="imm-lead" delay={360 + i * 120}>{p}</Reveal>
        ))}
        <Reveal delay={760}>
          <div style={{ marginTop: 34 }}><CtaCluster showPromo /></div>
          <div className="imm-scrollhint" data-testid="scroll-hint"><span>Explore</span><span className="chev"><Icon name="chevron-down" size={16} /></span></div>
        </Reveal>
      </section>

      <section className="imm-statement imm-wrap" data-testid="landing-benefit">
        <Reveal as="p">
          Stop calling home-care agencies one by one. Tell HomecareMatches what you need, and we reach out to partner agencies to help find <span className="accent">available caregivers</span> who may match your needs and preferences.
        </Reveal>
      </section>

      <section className="imm-wrap" data-testid="landing-solutions" style={{ paddingBottom: "6vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 8 }}>Home care solutions</Reveal>
        <Reveal as="h2" delay={80}>Our Home Care Solutions</Reveal>
        <Reveal as="p" delay={140} className="imm-lead">From personal and companion care to specialized dementia and overnight support, our partner agencies deliver a full range of in-home care services.</Reveal>
        <div className="imm-svc-grid" style={{ marginTop: 26 }}>
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 45}>
              <Link className="imm-svc-card" to={`/home-care-services#${s.slug}`} data-testid={`svc-${s.slug}`}>
                <span className="imm-svc-ic"><Icon name={s.icon} size={22} /></span>
                <h3>{s.name}</h3>
                <p>{s.desc}</p>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}><p style={{ marginTop: 22 }}><Link className="imm-inline-link" to="/home-care-services" data-testid="link-all-services">Explore all Home Care Services <Icon name="arrow-right" size={15} /></Link></p></Reveal>
      </section>

      <section className="imm-wrap" data-testid="landing-senior-matching" style={{ paddingBottom: "6vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 8 }}>Senior care matching</Reveal>
        <Reveal as="h2" delay={80}>Senior Care Matching Made Simple</Reveal>
        <Reveal as="p" delay={140} className="imm-lead">Senior care matching considers your loved one's needs, schedule, location, and preferences, then connects you with partner agencies that may have an available caregiver. <Link className="imm-inline-link" to="/senior-care-matching">Learn about Senior Care Matching</Link>.</Reveal>
      </section>

      <section className="imm-wrap" data-testid="landing-how" style={{ paddingBottom: "6vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 6 }}>The process</Reveal>
        <Reveal as="h2" delay={80}>How HomecareMatches Works</Reveal>
        <div style={{ marginTop: 24 }}>
          {MATCH_STEPS.map((s, i) => (
            <Reveal key={i} delay={i * 60}><div className="imm-feature"><span className="fnum">{String(i + 1).padStart(2, "0")}</span><div><h3>{s.title}</h3><p>{s.desc}</p></div></div></Reveal>
          ))}
        </div>
        <Reveal delay={120}><p style={{ marginTop: 22 }}><Link className="imm-inline-link" to="/how-it-works" data-testid="link-how">See how our matching process works <Icon name="arrow-right" size={15} /></Link></p></Reveal>
      </section>

      <section className="imm-wrap" data-testid="landing-caregiver-matching" style={{ paddingBottom: "6vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 8 }}>Matching</Reveal>
        <Reveal as="h2" delay={80}>Caregiver Matching Based on Your Needs</Reveal>
        <Reveal as="p" delay={140} className="imm-lead">We share your care needs, schedule, location, language, and preferences with partner home-care agencies so they can check for an available caregiver who may be a good match. <Link className="imm-inline-link" to="/caregiver-matching">Learn about Caregiver Matching</Link>.</Reveal>
      </section>

      <section className="imm-wrap" data-testid="landing-why" style={{ paddingBottom: "6vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 6 }}>Why HomecareMatches</Reveal>
        <Reveal as="h2" delay={80}>Why Choose HomecareMatches?</Reveal>
        <div style={{ marginTop: 24 }}>
          {WHY.map((b, i) => (
            <Reveal key={i} delay={i * 55}><div className="imm-feature"><span className="fnum">{String(i + 1).padStart(2, "0")}</span><div><h3>{b.title}</h3><p>{b.desc}</p></div></div></Reveal>
          ))}
        </div>
      </section>

      <section className="imm-wrap" data-testid="landing-unsure" style={{ paddingBottom: "6vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 8 }}>Guidance</Reveal>
        <Reveal as="h2" delay={80}>Not Sure What Type of Home Care You Need?</Reveal>
        <Reveal as="p" delay={140} className="imm-lead">Schedule a conversation with a HomecareMatches representative. We will help you understand your options and determine the next step.</Reveal>
        <Reveal delay={200}><div className="imm-cta-row" style={{ marginTop: 20 }}>
          <CtaLink to="/book-appointment" label="Book an Appointment" icon="calendar" testID="unsure-book" />
          <CtaLink to="/intake" label="Get Matched Now" icon="heart" variant="ghost" testID="unsure-matched" event="get_matched" />
        </div></Reveal>
      </section>

      <section className="imm-wrap" data-testid="landing-faq" style={{ paddingBottom: "6vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 6 }}>Good to know</Reveal>
        <Reveal as="h2" delay={80}>Frequently Asked Questions About Home Care Services</Reveal>
        <div style={{ marginTop: 24 }}>
          {FAQS.slice(0, 5).map((f, i) => (
            <Reveal key={i} delay={i * 55}><div className="imm-feature" data-testid={`faq-${i}`}><span className="fnum"><Icon name="help-circle" size={20} /></span><div><h3>{f.q}</h3><p>{f.a}</p></div></div></Reveal>
          ))}
        </div>
        <Reveal delay={120}><p style={{ marginTop: 22 }}><Link className="imm-inline-link" to="/faqs" data-testid="link-faqs">Read all FAQs about home care services <Icon name="arrow-right" size={15} /></Link></p></Reveal>
      </section>

      <section className="imm-wrap" data-testid="landing-final-cta" style={{ paddingBottom: "8vh" }}>
        <Reveal><PromoBanner /></Reveal>
        <Reveal delay={80}><div style={{ marginTop: 22 }}><CtaCluster showTrust /></div></Reveal>
      </section>
    </ImmersiveShell>
  );
}
